import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { SmartTableActions } from '../../ChatDSTemplate/type';
import defaultCopyIcon from '@/pages/aigc/assets/svg/agent-ds-copy.svg';
import defaultExportIcon from '@/pages/aigc/assets/svg/agent-ds-export.svg';
import defaultSaveIcon from '@/pages/aigc/assets/svg/agent-ai-table-save.svg';
import defaultRefreshIcon from '@/pages/aigc/assets/svg/agent-ds-refresh.svg';
import aiPolishIcon from '@/pages/aigc/assets/svg/agent-ai-polish.svg';
import copySuccessIcon from '@/pages/aigc/assets/svg/agent-copy-success.svg';
import { Bubble } from 'tea-component';
import defaultFullScreenIcon from '@/pages/aigc/assets/svg/agent-full-screen.svg';
import exportExcelIcon from '@/pages/aigc/assets/svg/agent-export-excel.svg';
import exportCsvIcon from '@/pages/aigc/assets/svg/agent-export-csv.svg';

/** 表格导出类型枚举 */
export enum SmartTableExportType {
  Excel = 'excel',
  CSV = 'csv',
}

/**
 * 行列类型枚举
 */
export enum RowColType {
  ROW = 'row',
  COLUMN = 'column',
}

/**
 * 添加行列操作类型枚举
 */
export enum AddRowColActionType {
  ADD_BLANK = 'add_blank',
  SMART_FILL = 'smart_fill',
}

/**
 * 添加行列操作配置项接口
 */
export interface IAddRowColOptionItem {
  /** 操作类型标识 */
  key: AddRowColActionType;
  /** 显示文本 i18n key */
  labelKey: string;
  /** 图标类型：'icon' 使用 React 组件，'img' 使用图片 */
  iconType: 'icon' | 'img';
  /** 图标内容：React 组件或图片路径 */
  icon: React.ReactNode | string;
  /** 图标额外类名 */
  iconClassName?: string;
}

/**
 * 添加行列操作选项配置
 * 支持自定义图标和扩展
 */
export const ADD_ROW_COL_OPTIONS: IAddRowColOptionItem[] = [
  {
    key: AddRowColActionType.ADD_BLANK,
    labelKey: 'dba_smart_table_add_blank',
    iconType: 'icon',
    icon: <PlusOutlined />,
    iconClassName: 'dba-add-row-col-menu__icon--plus',
  },
  {
    key: AddRowColActionType.SMART_FILL,
    labelKey: 'dba_smart_table_smart_fill',
    iconType: 'img',
    icon: aiPolishIcon,
    iconClassName: 'dba-add-row-col-menu__icon--ai',
  },
];

/**
 * 获取行列类型的 i18n key
 */
export const getRowColTypeLabelKey = (type: RowColType): string => {
  return type === RowColType.ROW ? 'dba_smart_table_row' : 'dba_smart_table_column';
};

/**
 * 获取添加行列的完整标签 - 返回 i18n key
 * 需要外部使用 formatMessage 组合
 */
export const getAddRowColLabelKey = (option: IAddRowColOptionItem): string => {
  return option.labelKey;
};

/** 表格导出类型配置 */
export const getSmartTableExportTypes = (formatMessage: (descriptor: { id: string; }) => string) => [
  { label: formatMessage({ id: 'dba_smart_table_export_excel' }), value: SmartTableExportType.Excel, icon: exportExcelIcon },
  { label: formatMessage({ id: 'dba_smart_table_export_csv' }), value: SmartTableExportType.CSV, icon: exportCsvIcon },
];

/** 智能表格操作按钮配置 - 使用 i18n keys */
export const getSmartTableActionsList = (formatMessage: (descriptor: { id: string; }) => string, showCopyTips = false) => [
  {
    label: (
      <div className="dba-chat-ds-report-page-right-resent dba-smart-table-action-icon">
        <img src={defaultRefreshIcon} alt="" />
        {formatMessage({ id: 'dba_smart_table_reset' })}
      </div>
    ),
    key: SmartTableActions.Reset,
  },
  {
    label: (
      <Bubble
        visible={showCopyTips}
        content={
          <div className="dba-feedback-operation-copy-contenet">
            <img src={copySuccessIcon} alt="" style={{ width: 16, height: 16, marginRight: 5 }} /> {formatMessage({ id: 'dba_smart_table_copy_success' })}
          </div>
        }
      >
        <div className="dba-chat-ds-report-page-right-resent dba-smart-table-action-icon">
          <img src={defaultCopyIcon} alt="" />
        </div>
      </Bubble>
    ),
    key: SmartTableActions.Copy,
  },
  {
    label: (
      <div className="dba-chat-ds-report-page-right-resent dba-smart-table-action-icon">
        <img src={defaultExportIcon} alt="" />
      </div>
    ),
    key: SmartTableActions.Export,
  },
  {
    label: (
      <div className="dba-chat-ds-report-page-right-resent dba-smart-table-action-icon">
        <img src={defaultSaveIcon} alt="" />
      </div>
    ),
    key: SmartTableActions.Save,
  },
  {
    label: (
      <div className="dba-chat-ds-report-page-right-resent dba-smart-table-action-icon">
        <img src={defaultFullScreenIcon} alt="" />
      </div>
    ),
    key: SmartTableActions.FullScreen,
  },
];

/** 最大 Excel 行数限制 */
export const MAX_EXCEL_ROWS = 10000;

/** 默认空白行数 */
export const DEFAULT_EMPTY_ROW_COUNT = 200;

/** 默认空白列数 */
export const DEFAULT_EMPTY_COL_COUNT = 20;

